import React, { lazy, Component } from 'react';
import { SparklesIcon, MicIcon, FileTextIcon, ShieldAlertIcon } from 'lucide-react';
import { MultiModalAnimation } from './MultiModalAnimation';
const FeatureCard = ({
  icon,
  title,
  description,
  bullets,
  controlLine,
  imageUrl,
  customComponent,
  index
}) => {
  const isEven = index % 2 === 0;
  return <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 sm:gap-8 lg:gap-12 items-center`} data-id="element-99">
      <div className="flex-1 w-full" data-id="element-100">
        <div className="bg-white rounded-2xl p-5 sm:p-8 border border-gray-200 shadow-sm" data-id="element-101">
          <div className="bg-orange-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6" data-id="element-102">
            {icon}
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4" data-id="element-103">
            {title}
          </h3>
          <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed" data-id="element-104">
            {description}
          </p>
          <ul className="space-y-2 sm:space-y-3" data-id="element-105">
            {bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-106">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" data-id="element-107"></div>
                <span className="text-sm sm:text-base text-gray-700" data-id="element-108">
                  {bullet}
                </span>
              </li>)}
          </ul>
          {controlLine && <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100 text-xs sm:text-sm text-gray-500 italic" data-id="element-109">
              {controlLine}
            </div>}
        </div>
      </div>
      <div className="flex-1 w-full" data-id="element-110">
        <div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg" data-id="element-111">
          {customComponent ? customComponent : imageUrl ? <img src={imageUrl} alt={title} className="w-full h-auto" loading="lazy" width="800" height="600" data-id="element-112" /> : <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center" data-id="element-113">
              <p className="text-gray-400" data-id="element-114">Feature visualization</p>
            </div>}
        </div>
      </div>
    </div>;
};
const Features = () => {
  const features = [{
    icon: <SparklesIcon size={24} className="text-orange-500" data-id="element-115" />,
    title: 'AI-Enabled Form Creation',
    description: 'Create forms in minutes using AI:',
    bullets: ['Generate forms from voice, screenshots, or PDFs', 'Convert onboarding documents into structured forms', 'Automatically style forms to match your brand'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/3EGqFrdu512DGNteqCHWy5/AIFormCreationGIF.gif'
  }, {
    icon: <MicIcon size={24} className="text-orange-500" data-id="element-116" />,
    title: 'Multi-Modal Inputs — In a Single Form',
    description: 'Let users respond naturally:',
    bullets: ['Type or speak responses', 'Upload documents or images', 'Capture photos with their camera', 'AI maps everything into your schema automatically'],
    customComponent: <MultiModalAnimation data-id="element-117" />
  }, {
    icon: <FileTextIcon size={24} className="text-orange-500" data-id="element-118" />,
    title: 'Intelligent Document Inputs',
    description: 'Documents become structured inputs.',
    bullets: ['Upload PDFs, scans, images, or handwritten forms', 'AI extracts the fields you care about', 'Faster review. Fewer errors. No manual reconciliation.'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/pQbNfY5D7CH7kKfiWK7TSZ/pdffFileInputExtraction.png'
  }, {
    icon: <ShieldAlertIcon size={24} className="text-orange-500" data-id="element-119" />,
    title: 'Reliable AI Validation + Follow-Ups',
    description: 'More than required fields — validation that protects your data.',
    bullets: ['Catch missing, inconsistent, or suspicious entries', 'Ask smart follow-up questions only when needed'],
    imageUrl: 'https://cdn.magicpatterns.com/uploads/tz8BCjnR7vnjcmCref1oK5/AIValidationDateofBirth.png'
  }];
  return <section className="w-full bg-gradient-to-b from-white to-gray-50 py-[2.85rem] sm:py-[3.8rem] lg:py-[5.75rem]" id="features" data-id="element-120">
      <div className="container mx-auto px-4" data-id="element-121">
        <div className="text-center mb-10 sm:mb-16 lg:mb-20" data-id="element-122">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-123">
            Features That Transform Form Experiences
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-124">
            Upgrade conversion and data quality without sacrificing brand
            control or your existing workflow.
          </p>
        </div>
        <div className="space-y-12 sm:space-y-16 lg:space-y-24 max-w-7xl mx-auto" data-id="element-125">
          {features.map((feature, index) => <FeatureCard key={index} {...feature} index={index} data-id="element-126" />)}
        </div>
      </div>
    </section>;
};
export default Features;